/**
 * Short synthesized chimes for voice calls, so the owner can hear the call
 * open and then hear it connect without looking at the screen. Generated with
 * Web Audio (no asset to load), one shared context, and silently skipped when
 * audio is unavailable or blocked.
 */
type Chime = 'start' | 'connected' | 'ended';

// Each note: [frequency Hz, start offset s, length s]
const PATTERNS: Record<Chime, Array<[number, number, number]>> = {
  // Two rising notes: the call is opening.
  start: [[523.25, 0, 0.16], [659.25, 0.14, 0.22]],
  // Three brighter rising notes: connected, start talking.
  connected: [[659.25, 0, 0.12], [783.99, 0.11, 0.12], [1046.5, 0.22, 0.28]],
  // Two falling notes: the call closed.
  ended: [[659.25, 0, 0.14], [523.25, 0.13, 0.24]],
};

let ctx: AudioContext | null = null;
let remoteSource: MediaStreamAudioSourceNode | null = null;
let remoteGain: GainNode | null = null;

function context(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx || ctx.state === 'closed') ctx = new Ctor();
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  return ctx;
}

export function playVoiceChime(kind: Chime, volume = 0.18) {
  try {
    const ac = context();
    if (!ac) return;
    const t0 = ac.currentTime + 0.01;
    const master = ac.createGain();
    master.gain.value = volume;
    master.connect(ac.destination);
    for (const [freq, at, len] of PATTERNS[kind]) {
      const start = t0 + at;
      const env = ac.createGain();
      env.gain.setValueAtTime(0, start);
      env.gain.linearRampToValueAtTime(1, start + 0.012);
      env.gain.exponentialRampToValueAtTime(0.001, start + len);
      env.connect(master);
      // A sine body plus a quiet triangle an octave up gives a soft bell tone.
      const body = ac.createOscillator();
      body.type = 'sine';
      body.frequency.value = freq;
      body.connect(env);
      const shimmer = ac.createOscillator();
      shimmer.type = 'triangle';
      shimmer.frequency.value = freq * 2;
      const shimmerGain = ac.createGain();
      shimmerGain.gain.value = 0.18;
      shimmer.connect(shimmerGain).connect(env);
      body.start(start);
      shimmer.start(start);
      body.stop(start + len + 0.02);
      shimmer.stop(start + len + 0.02);
    }
    const last = PATTERNS[kind][PATTERNS[kind].length - 1];
    window.setTimeout(() => { try { master.disconnect(); } catch { /* already gone */ } }, (last[1] + last[2] + 0.2) * 1000);
  } catch {
    /* audio blocked or unavailable: the visual state still shows the call */
  }
}

/**
 * Play the bot's voice through the same Web Audio context as the chimes.
 * An <audio> tag with a WebRTC stream is what iOS paints as a black rectangle
 * in the middle of the screen, so the call never attaches one.
 */
export function playRemoteVoice(stream: MediaStream | null) {
  try {
    const ac = context();
    if (!ac) return;
    try { remoteSource?.disconnect(); } catch { /* already gone */ }
    remoteSource = null;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    if (!tracks.length) return;
    if (!remoteGain) {
      remoteGain = ac.createGain();
      remoteGain.gain.value = 1;
      remoteGain.connect(ac.destination);
    }
    remoteSource = ac.createMediaStreamSource(new MediaStream(tracks));
    remoteSource.connect(remoteGain);
  } catch {
    /* the call still shows; there is just no speaker audio */
  }
}

export function setRemoteVoiceMuted(muted: boolean) {
  if (remoteGain) remoteGain.gain.value = muted ? 0 : 1;
}
